// Imports for external libraries go here.
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import Glide from '@glidejs/glide';
import dynamic from 'next/dynamic';

import { useSession } from '@marriott/mi-headless-utils';
import { useClientEnvVarsStore, useWishListDetailsGlobalStore, WishListItemStoreType } from '@marriott/mi-store-utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { constructAdfOrRlmURL } from '@marriott/mi-shop-components/src/organisms';
import { Heading, Messages, RichText, Types } from '@marriott/mi-ui-library';

import { generateApolloClientHeaders, getBreakpoint } from '../../modules';
import { phoenixAccountGetPropertyDetailsById, phoenixAccountGetPropertyRatesByPropertyID } from '../../modules/graph';
import { useWishlistModalStore } from '../../modules/store/wishlistModalStore';
import {
  generateViewHotelUrl,
  generateRitzCarltonHotelUrl,
  ValidMarshCodes,
  ValidBrandIds,
  ritzCarltonBrandId,
  getImageDomain,
  getFallbackImage,
  WISHLIST_MODAL_TYPE,
  WISHLIST_MODAL,
} from '../../modules/utils';
import { getDirection } from '../../modules/utils/overviewHelper';
import { PageContext } from '../../modules/context/PageContext';
import { SkeletonLoaderV2 } from '../../molecules';
import { CarouselControls } from '../../molecules/CarouselControls';
import { getTodayAndTomorrowDates } from '../../modules/utils/wishlistHelper';
import {
  WishlistMostLovedPropertiesProps,
  PropertyDetailsByIdResponse,
  PropertyDetails,
  PropertyRatesByIdResponse,
  RateNode,
} from './WishlistMostLovedProperties.types';
import { StyledWishlistMostLovedProperties } from './WishlistMostLovedProperties.styles';
import { WishlistHeartButton } from './WishlistHeartButton/WishlistHeartButton';
import mockResponse from './__mock__/WishlistMostLovedPropertiesResponse.mock.json';
import mockRatesResponse from './__mock__/WishlistMostLovedPropertiesRates.mock.json';

// eslint-disable-next-line @nx/enforce-module-boundaries
const PropertyCard = dynamic(() => import('@marriott/mi-shop-components/molecules').then(mod => mod.PropertyCard));

export const WishlistMostLovedProperties: FC<WishlistMostLovedPropertiesProps> = ({ model, isAuthorMode }) => {
  const { session } = useSession();
  const sessionData = session?.data;
  const { envVarsObject } = useClientEnvVarsStore();
  const pageContext = useContext(PageContext);

  const dataLoaded = useRef<boolean>(false);
  const [propertiesData, setPropertiesData] = useState<Array<{ node?: PropertyDetails }>>([]);
  const [ratesData, setRatesData] = useState<PropertyRatesByIdResponse | null>(null);

  const { wishlist, hasFetchedWishListDetails, error: wishlistError } = useWishListDetailsGlobalStore(state => state);
  const { wishlistId } = useWishlistModalStore(state => state);

  const glideRefOutlet = useRef<HTMLDivElement>(null);
  const parentGlide = useRef<(Glide.Static & Glide.Properties) | undefined>(undefined);
  const parentGlideInitialized = useRef<boolean>(false);
  const resizeFrameRef = useRef<number | null>(null);

  const [breakpoint, setBreakpoint] = useState<string | undefined>(getBreakpoint());
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [noOfDots, setNoOfDots] = useState<number>(0);

  const propertyList = useMemo(() => model?.propertiesList ?? [], [model?.propertiesList]);

  const favoritedIds = new Set<string>();
  wishlist?.forEach((w: WishListItemStoreType) => {
    w.properties?.forEach(p => {
      const normalizedPropertyId = p.propertyId?.toLowerCase();
      if (normalizedPropertyId) {
        favoritedIds.add(normalizedPropertyId);
      }
    });
  });

  const skipQuery =
    useMemo(() => {
      return !sessionData || isAuthorMode;
    }, [sessionData, isAuthorMode]) || dataLoaded.current;

  const destroyParentGlide = useCallback(() => {
    if (parentGlide.current) {
      try {
        parentGlide.current.destroy();
      } catch (_) {
        // ignore destroy errors during lifecycle changes
      }
      parentGlide.current = undefined;
      parentGlideInitialized.current = false;
    }

    resizeFrameRef.current = window.requestAnimationFrame(() => {
      setViewportWidth(window.innerWidth);
      setBreakpoint(getBreakpoint());
      if (parentGlide.current) {
        try {
          parentGlide.current.destroy();
        } catch (_) {
          // ignore destroy errors on resize
        }
        parentGlide.current = undefined;
        parentGlideInitialized.current = false;
      }
      resizeFrameRef.current = null;
    });
  }, []);

  const handleResize = useCallback(() => {
    if (resizeFrameRef.current) {
      window.cancelAnimationFrame(resizeFrameRef.current);
    }

    resizeFrameRef.current = window.requestAnimationFrame(() => {
      setViewportWidth(window.innerWidth);
      setBreakpoint(getBreakpoint());
      destroyParentGlide();
      resizeFrameRef.current = null;
    });
  }, [destroyParentGlide]);

  // Author mode data + resize listener setup
  useEffect(() => {
    if (isAuthorMode) {
      const mockEdges = mockResponse?.data?.search?.properties?.searchByPropertyIds?.edges ?? [];
      setPropertiesData(mockEdges as Array<{ node?: PropertyDetails }>);
      setRatesData(mockRatesResponse.data);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeFrameRef.current) {
        window.cancelAnimationFrame(resizeFrameRef.current);
      }
      destroyParentGlide();
    };
  }, [destroyParentGlide, isAuthorMode, handleResize]);

  const [fetchRates, { loading: ratesLoading, error: ratesError }] = useLazyQuery<PropertyRatesByIdResponse>(
    phoenixAccountGetPropertyRatesByPropertyID,
    {
      fetchPolicy: 'no-cache',
      context: generateApolloClientHeaders(false, pageContext),
    }
  );

  const rateMap: Record<string, RateNode> = useMemo(() => {
    const edges = ratesData?.search?.lowestAvailableRates?.searchByPropertyIds?.edges ?? [];
    const map: Record<string, RateNode> = {};
    edges.forEach((edge: { node?: RateNode | null }) => {
      const node = edge?.node;
      const id = node?.property?.id;
      if (id && node) map[id] = node;
    });
    return map;
  }, [ratesData]);

  const { loading: propertiesLoading, error: propertiesError } = useQuery<PropertyDetailsByIdResponse>(
    phoenixAccountGetPropertyDetailsById,
    {
      variables: {
        searchByPropertyIdsSearch2: {
          ids: propertyList,
        },
      },
      context: generateApolloClientHeaders(false, pageContext),
      skip: skipQuery || !propertyList.length,
      onCompleted: (data: PropertyDetailsByIdResponse) => {
        dataLoaded.current = true;
        const edges = data?.search?.properties?.searchByPropertyIds?.edges ?? [];
        setPropertiesData(edges);
      },
    }
  );
  useEffect(() => {
    if (wishlistId) {
      const currentWishlist = wishlist?.find((w: WishListItemStoreType) => w.wishlistId === wishlistId) ?? null;

      const dates = getTodayAndTomorrowDates();
      const startDate = currentWishlist?.startDate?.slice(0, 10) || dates?.todayDate;
      const endDate = currentWishlist?.endDate?.slice(0, 10) || dates?.tomorrowDate;
      fetchRates({
        variables: {
          search: {
            ids: propertyList,
            options: {
              startDate: startDate,
              endDate: endDate,
              rateRequestTypes: [{ type: 'STANDARD', value: '' }],
              customerId: '',
            },
          },
          limit: propertyList.length,
          offset: null,
        },
      }).then(result => {
        if (result?.data) setRatesData(result.data);
      });
    }
  }, [wishlist, wishlistId, fetchRates, propertyList]);

  const isSkeletonVisible =
    !isAuthorMode && (!hasFetchedWishListDetails || propertiesLoading || (!!wishlistId && ratesLoading));
  const hasQueryError = !isAuthorMode && !!(propertiesError || ratesError);

  useEffect(() => {
    if (isSkeletonVisible || hasQueryError || propertiesData.length === 0 || !glideRefOutlet.current) {
      destroyParentGlide();
      return;
    }

    if (parentGlide.current?.type) {
      return;
    }

    const CARD_WIDTH = 343;
    const CARD_GAP = 24;
    const trackWidth = glideRefOutlet.current.offsetWidth;
    const fittingPerView = Math.max(1, Math.floor((trackWidth + CARD_GAP) / (CARD_WIDTH + CARD_GAP)));
    const peekAfter = Math.max(0, trackWidth - fittingPerView * CARD_WIDTH - (fittingPerView - 1) * CARD_GAP);

    setNoOfDots(Math.max(0, propertiesData.length - fittingPerView + 1));
    const direction = getDirection();
    parentGlide.current = new Glide(glideRefOutlet.current as unknown as string, {
      type: 'slider',
      gap: CARD_GAP,
      startAt: 0,
      autoplay: false,
      bound: true,
      direction,
      focusAt: 0,
      dragThreshold: false,
      perView: fittingPerView,
      peek: { before: 0, after: peekAfter },
      keyboard: true,
    }) as Glide.Static & Glide.Properties;
    parentGlideInitialized.current = true;
    parentGlide.current.mount();
  }, [destroyParentGlide, hasQueryError, isSkeletonVisible, propertiesData, breakpoint, viewportWidth]);

  if (!isAuthorMode && wishlistError) return null;

  return (
    <StyledWishlistMostLovedProperties
      data-testid="account-WishlistMostLovedProperties"
      data-component-name="o-account-WishlistMostLovedProperties"
      className="m-container-fullbleed"
    >
      <div className="wishlist-most-loved-properties-container container">
        {model?.propertiesHeader && (
          <Heading
            variation={Types.headingType.title}
            fontSize={Types.size.small}
            titleText={model.propertiesHeader}
            customClass="mb-4"
          />
        )}
        {hasQueryError && (
          <Messages messageType="warning" className="mt-4 mb-3 px-2">
            <RichText text={pageContext?.uxlErrorMessage} componentId="wmlp-uxl-error-msg" />
          </Messages>
        )}
        {isSkeletonVisible && !hasQueryError && (
          <SkeletonLoaderV2 className="wmlp__skeleton" data-testid="wmlp-skeleton" />
        )}
        {!isSkeletonVisible && !hasQueryError && propertiesData.length > 0 && (
          <div className="glideOutlet" ref={glideRefOutlet}>
            <div className="glide__track" data-glide-el="track">
              <ul className="glide__slides d-flex p-0 pb-3 mb-0 list-unstyled">
                {propertiesData.map(({ node }, index) => {
                  const propertyId = node?.id || '';
                  const propertyName = node?.basicInformation?.nameInDefaultLanguage ?? '';
                  const brandId = node?.basicInformation?.brand?.id ?? '';
                  const currentLocale = envVarsObject?.['ACCEPT_LANGUAGE'] ?? '';

                  const isExternal = ValidMarshCodes?.includes(propertyId) || ValidBrandIds?.includes(brandId);
                  const isRitzCarlton = brandId?.toLowerCase() === ritzCarltonBrandId;
                  const hotelUrl = isRitzCarlton
                    ? generateRitzCarltonHotelUrl(propertyId, propertyName, currentLocale)
                    : generateViewHotelUrl(propertyId, propertyName);

                  const hotelViewEdges = node?.media?.photoGallery?.hotelView?.edges ?? [];
                  const imageUrls = hotelViewEdges
                    .map(edge => edge?.node?.imageUrls?.square)
                    .filter((url): url is string => !!url)
                    .slice(0, 5);
                  const images = imageUrls.length
                    ? imageUrls.map(url => ({ defaultImageUrl: getImageDomain(currentLocale) + url }))
                    : [{ defaultImageUrl: getFallbackImage(currentLocale)?.Classic }];

                  const titleDetails = {
                    title: propertyName,
                    isTitleLink: true,
                    titleAriaLabel: propertyName,
                    titleClickCallback: () => window.open(hotelUrl, isExternal ? '_blank' : '_self'),
                    titleButtonChildNode: <span className="m-ellipsis-2lines t-subtitle-m">{propertyName}</span>,
                  };

                  const brandDetails = {
                    brandId,
                    label: node?.basicInformation?.brand?.name ?? '',
                  };

                  const tertiaryLinkDetails = {
                    isLink: true,
                    href: hotelUrl,
                    className: 'm-link-tertiary-button',
                    buttonCopy: model?.viewHotelLabel,
                    target: isExternal ? '_blank' : '_self',
                    placeLinkOnTopSection: false,
                  };

                  const rateNode = rateMap?.[propertyId];
                  const lowestRate = rateNode?.rates?.[0]?.rateModes?.lowestAverageRate;
                  const rawAmount = lowestRate?.amount?.amount;
                  const decimalPoint = lowestRate?.amount?.decimalPoint ?? 0;
                  const priceCurrency = lowestRate?.amount?.currency;
                  const formattedPrice =
                    rawAmount != null
                      ? (() => {
                          const actual = rawAmount / Math.pow(10, decimalPoint);
                          return actual % 1 === 0 ? String(Math.round(actual)) : actual.toFixed(decimalPoint);
                        })()
                      : undefined;

                  const ctaURL = constructAdfOrRlmURL(
                    model?.bookNowCtaPath || '',
                    '?isRateCalendar=true&propertyCode=',
                    '?propertyCode=',
                    !wishlistId,
                    propertyId,
                    priceCurrency || '',
                    false
                  );

                  const footerLinkDetails = {
                    href: ctaURL,
                    text: wishlistId ? model?.viewRateBtn : model?.bookNowCta,
                    className: `m-button-s mt-1 ${wishlistId ? 'm-button-primary' : 'm-button-secondary'}`,
                    ...(!!wishlistId && formattedPrice && priceCurrency
                      ? {
                          hasPrice: true,
                          priceValue: formattedPrice,
                          priceType: model?.ratesLabel?.replace('{0}', priceCurrency),
                          priceAriaLabel: `${formattedPrice} ${priceCurrency}`,
                          isPointSaver: false,
                        }
                      : {}),
                  };

                  const reviewStars = node?.reviews?.stars?.count;
                  const reviewCount = node?.reviews?.numberOfReviews?.count;
                  const formattedReviewCount = reviewCount
                    ? Number(reviewCount).toLocaleString(currentLocale || 'en-US')
                    : '';
                  const reviewsDetails =
                    reviewStars && reviewCount
                      ? {
                          reviewsText: model?.reviews?.replace('{0}', formattedReviewCount) || '',
                          reviewsAvg: `${reviewStars}`,
                          reviewsLink: hotelUrl,
                          reviewsLabel: model?.reviews
                            ? `${formattedReviewCount} ${model.reviews}`
                            : formattedReviewCount,
                        }
                      : undefined;

                  const isFav = !!propertyId && favoritedIds.has(propertyId.toLowerCase());

                  return (
                    <li className="glide__slide" key={node?.id ?? index}>
                      <div className="wmlp__card-wrapper">
                        <WishlistHeartButton
                          isFavorited={isFav}
                          dataTestId="wishlist-heart-button"
                          iconClass="icon-l icon-inverse"
                          animateOnActivateOnly={true}
                          onToggle={() => {
                            document.dispatchEvent(
                              new CustomEvent(WISHLIST_MODAL.OPEN_EVENT, {
                                detail: {
                                  modalType: WISHLIST_MODAL_TYPE.CREATE_UPDATE,
                                  action: WISHLIST_MODAL.ACTION_SAVE,
                                  propertyId,
                                },
                              })
                            );
                          }}
                        />
                        <PropertyCard
                          titleDetails={titleDetails}
                          brandDetails={brandDetails}
                          images={images}
                          tertiaryLinkDetails={tertiaryLinkDetails}
                          footerLinkDetails={footerLinkDetails}
                          reviewsDetails={reviewsDetails}
                          isVerticalCard={true}
                          addScrimToImage={true}
                          showMobileVariantOnly={true}
                          isMBOP={false}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
              <CarouselControls dots={noOfDots} />
            </div>
          </div>
        )}
      </div>
    </StyledWishlistMostLovedProperties>
  );
};
